import React from 'react';
import { useParams } from 'react-router-dom';
import { fullBlog } from '../app/blogs/lib/interface';
import { client, urlFor } from '../app/blogs/lib/sanity';
import { PortableText } from '@portabletext/react';
import { Img } from 'react-image';
import { ClipLoader } from 'react-spinners';
import { Copy, Facebook, Twitter, Linkedin, MessageCircleHeart } from 'lucide-react';
import RelatedArticles from '../components/RelatedArticles';

export const revalidate = 30;

async function getData(slug: string) {
  const query = `
    *[_type == "blog" && slug.current == '${slug}'] {
      "currentSlug": slug.current,
      title,
      content,
      titleImage
    }[0]`;
  return await client.fetch(query);
}

async function getRelatedArticles(slug: string) {
  const query = `
    *[_type == "blog" && slug.current != '${slug}'] | order(_createdAt desc)[0...3] {
      title,
      smallDescription,
      "currentSlug": slug.current,
      titleImage
    }`;
  return await client.fetch(query);
}

export default function BlogArticle() {
  const { slug } = useParams<{ slug?: string }>();
  const [data, setData] = React.useState<fullBlog | null>(null);
  const [related, setRelated] = React.useState<any[]>([]);
  const [readingProgress, setReadingProgress] = React.useState(0);

  React.useEffect(() => {
    async function fetchData() {
      if (slug) {
        const blogData = await getData(slug);
        const relatedArticles = await getRelatedArticles(slug);
        setData(blogData);
        setRelated(relatedArticles);
      }
    }
    fetchData();
  }, [slug]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
  };

  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const progress = (scrollTop / docHeight) * 100;
    setReadingProgress(progress);
  };

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const estimateReadingTime = (blocks: any[]) => {
    const wordsPerMinute = 200;
    const text = blocks.map(block => (block.children || []).map((child: any) => child.text).join(' ')).join(' ');
    const wordCount = text.split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  if (!data) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <ClipLoader color="#fff" size={48} />
      </div>
    );
  }

  const readingTime = estimateReadingTime(data.content);

  return (
    <>
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 h-1 bg-blue-500 z-50 transition-all duration-200 ease-out"
        style={{ width: `${readingProgress}%` }}
      />

      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 text-white">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_40px] gap-4 relative">

          {/* Blog Content */}
          <div className="space-y-6">
            <h1 className="text-4xl md:text-5xl font-bold text-center">{data.title}</h1>
            <p className="text-center text-gray-400 text-sm">Estimated read time: {readingTime} min</p>

            <div className="w-full overflow-hidden rounded-xl shadow-lg border border-gray-700 aspect-video">
              <Img src={urlFor(data.titleImage).url()} alt={data.title} className="w-full h-full object-cover" />
            </div>

            <div className="prose prose-invert max-w-none">
              <PortableText value={data.content} />
            </div>

            <RelatedArticles articles={related} />
          </div>

          {/* Vertical Sticky Share Icons (Desktop) */}
          <div className="hidden lg:flex flex-col gap-4 pt-2 sticky top-20 h-fit">
            <a href={`https://wa.me/?text=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer">
              <MessageCircleHeart className="text-green-400 hover:scale-110 transition-transform" />
            </a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer">
              <Linkedin className="text-blue-500 hover:scale-110 transition-transform" />
            </a>
            <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer">
              <Twitter className="text-sky-400 hover:scale-110 transition-transform" />
            </a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer">
              <Facebook className="text-blue-600 hover:scale-110 transition-transform" />
            </a>
            <button onClick={handleCopy}>
              <Copy className="text-gray-300 hover:text-white hover:scale-110 transition-transform" />
            </button>
          </div>
        </div>

        {/* Mobile Share Icons */}
        <div className="lg:hidden flex justify-center gap-4 mt-6">
          <a href={`https://wa.me/?text=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer">
            <MessageCircleHeart className="text-green-400" />
          </a>
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer">
            <Linkedin className="text-blue-500" />
          </a>
          <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer">
            <Twitter className="text-sky-400" />
          </a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer">
            <Facebook className="text-blue-600" />
          </a>
          <button onClick={handleCopy}>
            <Copy className="text-gray-300" />
          </button>
        </div>
      </div>
    </>
  );
}




